import { useCallback, useEffect, useState } from 'react';

/**
 * useRailState — four-mode size machine for adaptive widgets pinned to
 * the right rail (Procedure widget today; Task / Diagnosis / etc. when
 * they land in the rail in future PRs).
 *
 * Renamed from `useProcedureWidgetState` and namespaced per-widget so
 * different rail-mounted widgets don't stomp each other's persisted
 * size — the storage key is now `widget-rail-size:{visitId}:{kind}`
 * instead of the original `proc-disc-size:{visitId}`. Existing visits
 * lose their persisted size once (one-time cost; falls back to the
 * default `shrunk` mode).
 *
 * Modes:
 *   'shrunk'  — 64–80px badge; not focused
 *   'idle'    — ~340px steady state
 *   'focused' — ~380–480px working surface; rail has focus
 *   'canvas'  — full-screen overlay (set explicitly via expandToCanvas)
 *
 * Per-visit persistence: when `visitId` is supplied, the most recent
 * non-canvas size is stored under `widget-rail-size:{visitId}:{kind}`
 * in sessionStorage so reopening the panel lands in the same state.
 * sessionStorage (not localStorage) keeps the preference scoped to the
 * doctor's current shift — a fresh login starts from `shrunk`.
 */
export const RAIL_SIZES = ['shrunk', 'idle', 'focused', 'canvas'];

const PERSIST_KEY = (visitId, kind) => `widget-rail-size:${visitId}:${kind || 'default'}`;

function readPersisted(visitId, kind) {
  if (!visitId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage?.getItem(PERSIST_KEY(visitId, kind));
    if (raw && RAIL_SIZES.includes(raw) && raw !== 'canvas') return raw;
  } catch (_e) { /* sessionStorage may be unavailable in private mode */ }
  return null;
}

function writePersisted(visitId, kind, value) {
  if (!visitId || typeof window === 'undefined') return;
  try { window.sessionStorage?.setItem(PERSIST_KEY(visitId, kind), value); }
  catch (_e) { /* ignore */ }
}

export default function useRailState({ initial = 'shrunk', visitId = null, kind = 'default' } = {}) {
  const [size, setSizeRaw] = useState(() => readPersisted(visitId, kind) || initial);

  useEffect(() => {
    const persisted = readPersisted(visitId, kind);
    if (persisted) setSizeRaw(persisted);
  }, [visitId, kind]);

  useEffect(() => {
    if (size && size !== 'canvas') writePersisted(visitId, kind, size);
  }, [size, visitId, kind]);

  const setSize = useCallback((next) => {
    if (!RAIL_SIZES.includes(next)) return;
    setSizeRaw((prev) => (prev === 'canvas' && next !== 'canvas' ? prev : next));
  }, []);

  const expandToCanvas = useCallback(() => setSizeRaw('canvas'), []);
  const collapseToDisc = useCallback(() => setSizeRaw('shrunk'), []);

  return { size, setSize, expandToCanvas, collapseToDisc };
}
